import React from 'react';
import { Modal, Button, Form, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { ConnectorGroup } from 'models/ConnectorGroup';
import { Connector } from 'models/Connector';
import { useAuth } from 'hooks/useAuth';
import {
  getCarrierConnectionConfig,
  getCarrierConnectionDefaults,
  getCarrierRequiredFields,
  getCarrierUserInstruction,
  getCarrierDisplayName,
  formatConnectionFieldLabel,
} from 'services/carrierService';

const REQUIRED_FIELD_MESSAGE = 'Please fill out this field.';

type ConnectorAddFormProps = {
  show: boolean;
  handleClose: () => void;
  handleSave: (connector: Connector) => void;
  connectorGroupId?: number;
  connectorGroups?: ConnectorGroup[];
};

const carrierLogos = (require as any).context('./carrier-logos', false, /\.(png|jpe?g|svg)$/);

const getCarrierLogo = (carrier?: string) => {
  if (!carrier) return null;
  const normalized = carrier.trim().toLowerCase();
  if (!normalized) return null;
  try {
    return carrierLogos(`./${normalized}.png`);
  } catch {
    return null;
  }
};

const ConnectorAddForm: React.FC<ConnectorAddFormProps> = ({ show, handleClose, handleSave, connectorGroupId, connectorGroups }) => {
  const { accessToken } = useAuth();
  const fallbackConnectorGroupId = React.useMemo(() => {
    if (connectorGroupId && connectorGroupId > 0) return connectorGroupId;
    if (connectorGroups && connectorGroups.length > 0) return connectorGroups[0].id;
    return 0;
  }, [connectorGroupId, connectorGroups]);

  const [newConnector, setNewConnector] = React.useState<Connector>({
    id: 0,
    username: '',
    password: '',
    carrier: '',
    status: '',
    connector_group_id: fallbackConnectorGroupId,
    connector_group_name: '',
    enabled: true,
    api_key: '',
    endpoint: '',
    remote_directory: '',
  });

  const [carrierConfigs, setCarrierConfigs] = React.useState<any[]>([]);
  const [loadingConfig, setLoadingConfig] = React.useState<boolean>(false);
  const [configError, setConfigError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string[]>>({});
  const [passwordVisibility, setPasswordVisibility] = React.useState<Record<string, boolean>>({});

  const togglePasswordVisibility = (fieldName: string) => {
    setPasswordVisibility((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  // Get required fields for the selected carrier
  const requiredFields: string[] = React.useMemo(() => {
    if (!newConnector.carrier || !carrierConfigs.length) return [];
    return getCarrierRequiredFields(newConnector.carrier, carrierConfigs);
  }, [newConnector.carrier, carrierConfigs]);

  const connectionDefaults = React.useMemo(
    () =>
      newConnector.carrier
        ? getCarrierConnectionDefaults(newConnector.carrier, carrierConfigs)
        : {},
    [newConnector.carrier, carrierConfigs],
  );

  const carrierInstructionText = React.useMemo(() => {
    if (!newConnector.carrier) return 'Please choose a carrier and connector group.';
    return getCarrierUserInstruction(newConnector.carrier, carrierConfigs) || 'Please choose a carrier and connector group.';
  }, [newConnector.carrier, carrierConfigs]);

  const carrierLogoSrc = React.useMemo(
    () => getCarrierLogo(newConnector.carrier),
    [newConnector.carrier]
  );

  const selectedCarrierDisplayName = React.useMemo(
    () => getCarrierDisplayName(newConnector.carrier, carrierConfigs),
    [newConnector.carrier, carrierConfigs],
  );

  // Check if selected carrier has email collection method
  const isEmailCollectionMethod = React.useMemo(() => {
    if (!newConnector.carrier || !carrierConfigs.length) return false;
    const carrier = carrierConfigs.find(config => config.name === newConnector.carrier);
    return carrier?.collection_method === 'email';
  }, [newConnector.carrier, carrierConfigs]);

  React.useEffect(() => {
    if (!show) return;
    if (!accessToken) return;

    let ignore = false;
    const loadCarrierConfigs = async () => {
      setLoadingConfig(true);
      setConfigError(null);
      try {
        const data = await getCarrierConnectionConfig(accessToken);
        if (!ignore) {
          setCarrierConfigs(data);
        }
      } catch (e: any) {
        if (!ignore) setConfigError(e?.message ?? 'Failed to load carrier config');
      } finally {
        if (!ignore) setLoadingConfig(false);
      }
    };

    loadCarrierConfigs();
    return () => { ignore = true; };
  }, [show, accessToken]);

  React.useEffect(() => {
    if (!show) return;
    if (connectorGroupId && connectorGroupId > 0) {
      setNewConnector(prev => ({ ...prev, connector_group_id: connectorGroupId }));
      return;
    }
    if (fallbackConnectorGroupId && fallbackConnectorGroupId > 0 && newConnector.connector_group_id === 0) {
      setNewConnector(prev => ({ ...prev, connector_group_id: fallbackConnectorGroupId }));
    }
  }, [show, connectorGroupId, fallbackConnectorGroupId, newConnector.connector_group_id]);

  const submitButtonRef = React.useRef<HTMLButtonElement>(null);
  const usernameInputRef = React.useRef<HTMLInputElement>(null);

  const focusFieldAfterCarrierSelect = (carrierValue: string) => {
    const fields = getCarrierRequiredFields(carrierValue, carrierConfigs);
    if (fields.length > 0) return;

    const carrier = carrierConfigs.find((config) => config.name === carrierValue);
    const isEmail = carrier?.collection_method === 'email';

    requestAnimationFrame(() => {
      if (isEmail) {
        submitButtonRef.current?.focus();
      } else {
        usernameInputRef.current?.focus();
      }
    });
  };

  // Correct the type of handleChange to match react-bootstrap's expectations
  const handleChange: React.ChangeEventHandler<any> = (e) => {
    const { name, value } = e.target;
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (name === 'connector_group_id') {
      const parsed = value ? Number(value) : 0;
      setNewConnector({ ...newConnector, connector_group_id: parsed });
      return;
    }
    if (name === 'carrier') {
      setNewConnector({ ...newConnector, carrier: value });
      (e.target as HTMLSelectElement).blur();
      focusFieldAfterCarrierSelect(value);
      return;
    }
    setNewConnector({ ...newConnector, [name]: value });
  };

  const validateConnectorFields = (): Record<string, string[]> => {
    const errors: Record<string, string[]> = {};

    if (!isEmailCollectionMethod && !newConnector.username.trim()) {
      errors.username = [REQUIRED_FIELD_MESSAGE];
    }

    requiredFields.forEach((fieldName) => {
      const hasDefault = connectionDefaults[fieldName] !== undefined;
      if (hasDefault) return;

      const value = newConnector[fieldName as keyof Connector];
      if (!String(value || '').trim()) {
        errors[fieldName] = [REQUIRED_FIELD_MESSAGE];
      }
    });

    return errors;
  };

  const handleSubmit = () => {
    if (!newConnector.connector_group_id) return;

    const errors = validateConnectorFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    handleSave(newConnector);
    handleClose();
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleCloseAndReset = () => {
    setFieldErrors({});
    setPasswordVisibility({});
    setNewConnector({
      id: 0,
      username: '',
      password: '',
      carrier: '',
      status: '',
      connector_group_id: fallbackConnectorGroupId,
      connector_group_name: '',
      enabled: true,
      api_key: '',
      endpoint: '',
      remote_directory: '',
    });
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleCloseAndReset}>
      <Form noValidate onSubmit={handleFormSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Connector</Modal.Title>
        </Modal.Header>
        <Modal.Body>
        {newConnector.carrier && carrierLogoSrc && (
          <div className="mb-2 d-flex justify-content-center">
            <img
              src={carrierLogoSrc}
              alt={`${selectedCarrierDisplayName} logo`}
              style={{ width: 80, height: 80, objectFit: 'contain' }}
            />
          </div>
        )}
        <div className="text-700 mb-3">
          {carrierInstructionText}
        </div>
          {!connectorGroupId && connectorGroups && connectorGroups.length > 0 && (
            <Form.Group controlId="formConnectorGroup">
              <Form.Label>Group/ConnectorGroup</Form.Label>
              <Form.Select
                name="connector_group_id"
                value={newConnector.connector_group_id || ''}
                onChange={handleChange}
                required
              >
                <option value="">Select a Connector Group</option>
                {connectorGroups.map(connectorGroup => (
                  <option key={connectorGroup.id} value={connectorGroup.id}>
                    {connectorGroup.display_name || connectorGroup.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group controlId="formCarrier">
            <Form.Label>Carrier</Form.Label>
            <Form.Select
              name="carrier"
              value={newConnector.carrier}
              onChange={handleChange}
              required
            >
              <option value="">Select a carrier</option>
              {carrierConfigs.map((carrier: any) => (
                <option key={carrier.name} value={carrier.name}>
                  {getCarrierDisplayName(carrier.name, carrierConfigs)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {!isEmailCollectionMethod && (
            <Form.Group controlId="formConnectorUserName">
              <Form.Label>UserName</Form.Label>
              <Form.Control
                ref={usernameInputRef}
                type="text"
                name="username"
                value={newConnector.username}
                onChange={handleChange}
                isInvalid={Boolean(fieldErrors.username)}
              />
              {fieldErrors.username && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.username.join(' ')}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          )}

          {loadingConfig && (
            <div className="d-flex align-items-center gap-2 mb-2"><Spinner size="sm" animation="border" /> Loading config...</div>
          )}
          {configError && (
            <div className="text-danger mb-2">{configError}</div>
          )}
          {/* Dynamic fields based on carrier requirements */}
          {requiredFields.map((fieldName: string) => {
            const isPassword = fieldName.toLowerCase().includes('password');
            const showPassword = passwordVisibility[fieldName];
            const defaultForField = connectionDefaults[fieldName];
            const hasDefault = defaultForField !== undefined;
            const labelText = formatConnectionFieldLabel(fieldName);
            const fieldValue = typeof newConnector[fieldName as keyof Connector] === 'string'
              ? newConnector[fieldName as keyof Connector] as string
              : '';
            const placeholder = hasDefault ? defaultForField : `Enter ${fieldName.replace(/_/g, ' ')}`;
            return (
              <Form.Group key={fieldName} controlId={`form${fieldName}`}>
                <Form.Label>
                  {hasDefault ? `${labelText} (optional)` : labelText}
                </Form.Label>
                {isPassword ? (
                  <InputGroup hasValidation>
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      name={fieldName}
                      value={fieldValue}
                      onChange={handleChange}
                      placeholder={placeholder}
                      isInvalid={Boolean(fieldErrors[fieldName])}
                    />
                    <Button
                      variant="outline-primary"
                      type="button"
                      onClick={() => togglePasswordVisibility(fieldName)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </Button>
                    {fieldErrors[fieldName] && (
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors[fieldName].join(' ')}
                      </Form.Control.Feedback>
                    )}
                  </InputGroup>
                ) : (
                  <>
                    <Form.Control
                      type="text"
                      name={fieldName}
                      value={fieldValue}
                      onChange={handleChange}
                      placeholder={placeholder}
                      isInvalid={Boolean(fieldErrors[fieldName])}
                    />
                    {fieldErrors[fieldName] && (
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors[fieldName].join(' ')}
                      </Form.Control.Feedback>
                    )}
                  </>
                )}
              </Form.Group>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            ref={submitButtonRef}
            variant="primary"
            type="submit"
            disabled={!newConnector.connector_group_id}
          >
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ConnectorAddForm;
